"use client";

import { useState } from "react";
import { Search, Settings as SettingsIcon, LogOut } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { BattleNetIcon } from "@/components/icons/BattleNetIcon";
import { ClassSpecPicker } from "./ClassSpecPicker";
import { useCharState, setContentFilter } from "@/lib/state";
import { useAuth, logout } from "@/lib/auth";
import { cn } from "@/lib/utils";
import type { ContentFilter } from "@/lib/types";

const FILTERS: { value: ContentFilter; label: string }[] = [
  { value: "both",  label: "All" },
  { value: "mplus", label: "M+" },
  { value: "raid",  label: "Raid" },
];

export function Topbar() {
  const state = useCharState();
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className={cn(
        "sticky top-0 z-20 h-14 border-b border-border",
        "bg-bg/80 backdrop-blur-sm flex items-center gap-3 px-4 lg:px-6",
      )}
    >
      <div className="shrink-0">
        <ClassSpecPicker />
      </div>

      <div className="hidden md:flex items-center gap-1 rounded-sm border border-border bg-surface p-0.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setContentFilter(f.value)}
            className={cn(
              "h-7 px-2.5 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
              state.contentFilter === f.value
                ? "bg-surface-3 text-fg"
                : "text-fg-subtle hover:text-fg hover:bg-surface-2",
            )}
            title={`Show ${f.label} pools`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 max-w-sm hidden lg:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
          <Input placeholder="Search items, bosses..." className="h-9 pl-9 text-xs" />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1 relative">
        {/* Battletag chip when signed in. */}
        {auth.state === "signed-in" && (
          <span
            className={cn(
              "hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm",
              "bg-[#148eff]/10 border border-[#148eff]/40 text-[#148eff]",
              "font-mono text-[11px] uppercase tracking-wider",
            )}
          >
            <BattleNetIcon size={12} />
            {auth.user.battletag}
          </span>
        )}

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={cn(
            "inline-flex items-center justify-center h-9 w-9 rounded-sm",
            "text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors",
          )}
          aria-label="Settings menu"
        >
          <SettingsIcon className="h-4 w-4" />
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden />
            <div
              className={cn(
                "absolute right-0 top-11 z-50 w-60 rounded-md border border-border-strong bg-surface",
                "shadow-[0_12px_40px_-12px_rgba(0,0,0,0.8)] py-1",
              )}
            >
              {auth.state === "signed-in" ? (
                <>
                  <p className="eyebrow px-3 py-2">Signed in</p>
                  <p className="px-3 pb-2 text-xs text-fg font-mono break-all">
                    {auth.user.battletag}
                  </p>
                  <p className="px-3 pb-2 text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                    {auth.user.region.toUpperCase()} region
                  </p>
                  <hr className="hairline" />
                  <a
                    href="/settings"
                    className="block px-3 py-2 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg transition-colors"
                  >
                    Settings
                  </a>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); void logout(); }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-xs",
                      "text-fg-muted hover:bg-surface-2 hover:text-danger transition-colors",
                      "inline-flex items-center gap-2",
                    )}
                  >
                    <LogOut className="h-3 w-3" />
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <p className="eyebrow px-3 py-2">Mode</p>
                  <p className="px-3 pb-2 text-xs text-fg-muted">
                    {auth.state === "loading" ? "Checking..." : "Local (no Battle.net)"}
                  </p>
                  <hr className="hairline" />
                  <a
                    href="/login"
                    className={cn(
                      "block px-3 py-2 text-xs",
                      "text-[#148eff] hover:bg-surface-2 transition-colors",
                      "inline-flex items-center gap-2",
                    )}
                  >
                    <BattleNetIcon size={12} />
                    Sign in with Battle.net
                  </a>
                  <a
                    href="/settings"
                    className="block px-3 py-2 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg transition-colors"
                  >
                    Settings
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
}
